<!-- src/components/Common/CustomMultiSelect.vue -->
<template>
  <div class="relative w-full" ref="containerRef">
    <!-- 
        Added 'box-border' to ensure padding doesn't affect width calculations.
        The styling from the parent's :deep() selector will target this element.
      -->
    <div
      class="multiselect-dropdown box-border border transition-colors duration-200 ease-in-out"
      @click="toggleDropdown"
      ref="triggerRef"
      :class="[{ 'cursor-not-allowed opacity-50': disabled }, statusClass]"
      :title="unlistedTitle || undefined"
      role="button"
      aria-haspopup="listbox"
      :aria-expanded="isOpen.toString()"
    >
      <span v-if="displayOverride" class="optext">{{ displayOverride }}</span>
      <span v-else-if="selectedOptions.length === 0" class="placeholder">{{ placeholder }}</span>
      <span v-else class="optext">{{ displayText }}</span>
      <!-- Hidden measurer for text width -->
      <span ref="measureRef" class="optext invisible absolute -z-10">{{ labelListText }}</span>
    </div>

    <div v-if="displayOnlySelections.length" class="mt-1 flex flex-wrap gap-1">
      <button
        v-for="option in displayOnlySelections"
        :key="option.value"
        type="button"
        class="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500"
        :disabled="disabled"
        :aria-label="`Remove ${option.baseLabel || option.label}`"
        @click.stop="removeStoredOption(option)"
      >
        {{ option.label }}
        <X class="icon-xs" />
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="multiselect-dropdown-list-wrapper"
        ref="listWrapperRef"
        :style="listStyle"
      >
        <div class="multiselect-dropdown-list" role="listbox" aria-multiselectable="true">
          <!-- Optional header (e.g. saved Views) — scrolls together with the list -->
          <slot name="header" :close="closeDropdown" />
          <!-- Search box -->
          <div class="multiselect-search-wrapper">
            <input
              v-model="searchTerm"
              type="text"
              class="multiselect-search-input"
              :placeholder="searchPlaceholder || 'Search…'"
              @keydown.stop
            />
          </div>

          <!-- Options -->
          <div
            v-for="option in displayedOptions"
            :key="optionKey(option)"
            class="multiselect-option"
            :class="{
              selected: isSelectable(option) && isSelected(option.value),
              'is-divider': option.isDivider,
              'is-disabled': option.disabled,
              'is-unlisted': option.unlisted,
            }"
            :title="option.title || undefined"
            role="option"
            :aria-selected="isSelectable(option) ? isSelected(option.value).toString() : 'false'"
            @click="toggleOption(option)"
          >
            <!-- Divider / section header row -->
            <template v-if="option.isDivider">
              <div class="multiselect-section-header">
                <span class="multiselect-section-label">{{ option.label }}</span>
                <div class="multiselect-divider"></div>
              </div>
            </template>

            <!-- Normal option -->
            <template v-else>
              <input
                v-if="isSelectable(option)"
                type="checkbox"
                :checked="isSelected(option.value)"
                tabindex="-1"
              />
              <label>{{ option.label }}</label>
            </template>
          </div>

          <!-- No results found state -->
          <div v-if="displayedOptions.length === 0" class="p-3 text-center text-xs text-gray-500">
            No options found.
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { X } from 'lucide-vue-next'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Select options…' },
  disabled: { type: Boolean, default: false },
  isComplete: { type: Boolean, default: false },
  noneValue: { type: String, default: 'None' },
  placement: { type: String, default: 'auto' },
  maxListHeight: { type: Number, default: 240 },
  searchPlaceholder: { type: String, default: '' },
  // When set, the trigger shows this text instead of the selected-count/labels
  // (e.g. the active View name). Empty string = default behavior.
  displayOverride: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const containerRef = ref(null)
const listWrapperRef = ref(null)
const triggerRef = ref(null)
const measureRef = ref(null)
const showCount = ref(false)
const listStyle = ref({})
const searchTerm = ref('')

// Helpers
const isSelectable = option => !option?.isDivider && !option?.disabled

const selectedOptions = computed(() =>
  props.options.filter(opt => isSelectable(opt) && props.modelValue.includes(opt.value))
)

const labelListText = computed(() => selectedOptions.value.map(o => o.label).join(', '))
const displayOnlySelections = computed(() =>
  selectedOptions.value.filter(option => option.displayOnly)
)
function removeStoredOption(option) {
  if (props.disabled) return
  emit(
    'update:modelValue',
    props.modelValue.filter(value => value !== option.value)
  )
}

// Why any selected pick is no longer offered (options flagged `unlisted` by useChoiceOptions), for the trigger's tooltip.
const unlistedTitle = computed(() =>
  selectedOptions.value
    .filter(o => o.unlisted && o.title)
    .map(o => o.title)
    .join('\n')
)

const displayText = computed(() => {
  const count = selectedOptions.value.length
  if (count === 0) return props.placeholder
  if (showCount.value) return `${count} Selected`
  return labelListText.value
})

const statusClass = computed(() =>
  props.isComplete ? 'border-green-400 ring-1 ring-green-200' : 'border-gray-300'
)

const isSelected = optionValue => props.modelValue.includes(optionValue)

const displayedOptions = computed(() => {
  const raw = (props.options || []).filter(option => !option.displayOnly)
  const term = (searchTerm.value || '').trim().toLowerCase()

  if (!term) return raw

  return raw.filter(opt => {
    if (opt.isDivider) return true
    if (!opt.label) return false
    return String(opt.label).toLowerCase().includes(term)
  })
})

const optionKey = option =>
  option.value != null ? option.value : `divider-${Math.random().toString(36).slice(2)}`

const positionDropdown = async () => {
  await nextTick()
  if (!containerRef.value || !listWrapperRef.value) return

  const rect = containerRef.value.getBoundingClientRect()
  const listEl = listWrapperRef.value

  // Reset basic styles for calculation
  listEl.style.position = 'fixed'
  listEl.style.visibility = 'hidden'
  listEl.style.maxHeight = `${props.maxListHeight}px`
  listEl.style.minWidth = `${rect.width}px`
  listEl.style.width = 'auto'

  const desiredHeight = Math.min(listEl.scrollHeight || props.maxListHeight, props.maxListHeight)
  const vwHeight = window.innerHeight
  const vwWidth = window.innerWidth
  const spaceBelow = vwHeight - rect.bottom - 6
  const spaceAbove = rect.top - 6

  let place = props.placement
  if (props.placement === 'auto') {
    place = spaceBelow >= desiredHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top'
  }

  let top = 0
  if (place === 'bottom') {
    top = rect.bottom + 4
  } else {
    top = rect.top - desiredHeight - 4
  }

  // Horizontal positioning constraint
  const listRectWidth = listEl.scrollWidth
  let left = rect.left

  if (left + listRectWidth > vwWidth - 10) {
    left = vwWidth - listRectWidth - 10
  }
  if (left < 10) left = 10

  listStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${rect.width}px`,
    width: 'auto',
    maxHeight: `${props.maxListHeight}px`,
    visibility: 'visible',
    zIndex: 9999,
  }
}

const recomputeDisplayMode = async () => {
  await nextTick()
  const trigger = triggerRef.value
  const measurer = measureRef.value
  if (!trigger || !measurer) {
    showCount.value = false
    return
  }
  const cs = getComputedStyle(trigger)
  const paddingLeft = parseFloat(cs.paddingLeft) || 0
  const paddingRight = parseFloat(cs.paddingRight) || 0
  // Subtract chevron width (approx 24px)
  const available = trigger.clientWidth - paddingLeft - paddingRight - 20
  const contentWidth = measurer.offsetWidth
  showCount.value = contentWidth > available
}

const toggleDropdown = async () => {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    searchTerm.value = ''
    await positionDropdown()
  }
}

// Exposed to the #header slot so it can close the dropdown (e.g. before opening a
// modal that would otherwise sit behind the teleported list's high z-index).
function closeDropdown() {
  isOpen.value = false
}

const toggleOption = option => {
  if (!isSelectable(option) || option.displayOnly) return

  const isNone = option.value === props.noneValue
  const current = new Set(props.modelValue)

  if (isNone) {
    emit('update:modelValue', current.has(props.noneValue) ? [] : [props.noneValue])
  } else {
    current.delete(props.noneValue)
    if (current.has(option.value)) current.delete(option.value)
    else current.add(option.value)
    emit('update:modelValue', Array.from(current))
  }
}

watch(
  () => props.modelValue,
  () => {
    recomputeDisplayMode()
  },
  { deep: true }
)

const handleClickOutside = event => {
  if (
    isOpen.value &&
    containerRef.value &&
    !containerRef.value.contains(event.target) &&
    listWrapperRef.value &&
    !listWrapperRef.value.contains(event.target)
  ) {
    isOpen.value = false
  }
}

const handleViewportChange = () => {
  if (!isOpen.value) return
  positionDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  window.addEventListener('scroll', handleViewportChange, true)
  window.addEventListener('resize', handleViewportChange)
  recomputeDisplayMode()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  window.removeEventListener('scroll', handleViewportChange, true)
  window.removeEventListener('resize', handleViewportChange)
})
</script>

<style scoped>
.multiselect-dropdown {
  display: flex;
  align-items: center;
  padding: 0.375rem 2.5rem 0.375rem 0.625rem;
  border-radius: 6px;
  background-color: white;
  position: relative;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px 12px;
  width: 100%;
  min-height: 38px;
  height: 38px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
}

.multiselect-dropdown span.optext {
  font-size: 0.75rem;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
}

.multiselect-dropdown span.placeholder {
  color: #9ca3af;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Teleported list container */
.multiselect-dropdown-list-wrapper {
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10000;
  padding: 4px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  margin-top: 4px;
  background: white;
  min-width: max-content;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.multiselect-dropdown-list {
  padding: 2px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
}

.multiselect-dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.multiselect-dropdown-list::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}

/* Search box */
.multiselect-search-wrapper {
  padding: 4px 8px 6px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  flex-shrink: 0;
}

.multiselect-search-input {
  width: 100%;
  padding: 6px 8px;
  font-size: 0.75rem;
  border-radius: 4px;
  border: 1px solid #d1d5db;
}

.multiselect-search-input:focus {
  outline: 2px solid #e0e7ff;
  border-color: #6366f1;
}

/* Options */
.multiselect-option {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s;
}

.multiselect-option.selected {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.multiselect-option.is-divider {
  cursor: default;
  padding: 0;
}

.multiselect-option.is-disabled {
  cursor: default;
  opacity: 0.5;
}

/* A saved pick the list no longer offers (src/utils/choiceStoredValues.js): shown so it can be seen and removed. */
.multiselect-option.is-unlisted label {
  color: #6b7280;
  font-style: italic;
}

.multiselect-dropdown-list input[type='checkbox'] {
  height: 1rem;
  width: 1rem;
  margin-right: 0.75rem;
  border-radius: 3px;
  border-color: #d1d5db;
  color: #4f46e5;
  pointer-events: none;
  flex-shrink: 0;
}

.multiselect-dropdown-list label {
  pointer-events: none;
  font-size: 0.8125rem;
  color: #374151;
  white-space: nowrap;
}

.multiselect-option:hover:not(.is-divider):not(.is-disabled) {
  background-color: #f3f4f6;
}

/* Divider row */
.multiselect-divider {
  height: 1px;
  width: 100%;
  background-color: #e5e7eb;
  margin: 4px 0;
}

.multiselect-section-header {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin-top: 2px;
  margin-bottom: 2px;
}

.multiselect-section-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  margin-right: 8px;
  white-space: nowrap;
}
</style>
